import { createRootRouteWithContext } from "@tanstack/react-router";
import i18next from "i18next";
import * as React from "react";
import { TooltipProvider } from "ui";

import { AuthContextProvider } from "src/auth";
import { AuthenticationStatusMonitor } from "src/components/AuthenticationStatusMonitor";
import { DefaultCatchBoundary } from "src/components/DefaultCatchBoundary";
import { GlobalNavigationInfoBadge } from "src/components/GlobalNavigationInfoBadge";
import { GlobalNavigationProgress } from "src/components/GlobalNavigationProgress";
import { LanguageSwitchProvider } from "src/components/LanguageSwitchContext";
import { NotFound } from "src/components/NotFound";
import { Document } from "src/components/RootRouteDocument";
import { PosthogInit } from "src/components/RootRoutePosthogInit";
import { SentryUser } from "src/components/RootRouteSentryUser";
import { TimeProviderWithAuth } from "src/components/RootRouteTimeProviderWithAuth";
import { UpdateAvailableDialog } from "src/components/UpdateAvailableDialog";
import { AppMetadataProvider } from "src/contexts/AppMetadataContext";
import { CookieConsentProvider } from "src/contexts/CookieConsentContext";
import appCss from "src/globals.css?url";
import { RootLayout } from "src/layout/RootLayout";
import {
  CONSENT_COOKIE_NAME,
  parseConsentFromCookie,
} from "src/lib/cookieConsent";
import { createAppMetadata } from "src/lib/createAppMetadata";
import { getVersion } from "src/lib/getVersion";
import { updateIsomorphicRequestData } from "src/lib/isomorphicRequestData/isomorphicRequestDataRef";
import { loaderHandler } from "src/lib/loaderHandler";
import { billingProductsQueryOptions } from "src/queries/billing";
import { trpcClient } from "src/trpc";
import { type RootRouteContext } from "src/types";

export const Route = createRootRouteWithContext<RootRouteContext>()({
  head: () => ({
    meta: [
      {
        title: "sztama",
      },
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#000" },
    ],
  }),
  errorComponent: DefaultCatchBoundary,
  notFoundComponent: () => <NotFound />,
  shellComponent: RootDocument,
  ssr: true,
  loader: async ({ context }) => {
    return loaderHandler(async (initialData) => {
      const version = await getVersion();

      if (
        initialData.session?.coach &&
        initialData.session.user.tier === "FREE"
      ) {
        await context.queryClient.query({
          ...billingProductsQueryOptions(),
          staleTime: "static",
        });
      }

      function parseConsentCookie() {
        if (!initialData.cookieHeader) {
          return null;
        }

        const cookies = initialData.cookieHeader.split(";");

        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split("=");

          if (name === CONSENT_COOKIE_NAME) {
            return parseConsentFromCookie(decodeURIComponent(value));
          }
        }

        return null;
      }

      return {
        data: {
          initialData: {
            session: initialData.session,
            locale: initialData.locale,
            posthogBootstrap: initialData.posthogBootstrap,
            metadata: createAppMetadata(version),
            cookieConsent: parseConsentCookie(),
          },
        },
      };
    });
  },
});

function RootDocument({ children }: { children: React.ReactNode }) {
  const loaderData = Route.useLoaderData();

  const initialData = loaderData.data.initialData;

  React.useEffect(() => {
    updateIsomorphicRequestData({
      locale: initialData.locale,
      posthogBootstrap: initialData.posthogBootstrap,
      session: initialData.session,
      i18n: i18next,
      trpc: trpcClient,
    });
  }, []);

  return (
    <Document lang={initialData.locale}>
      <AppMetadataProvider metadata={initialData.metadata}>
        <CookieConsentProvider initialConsent={initialData.cookieConsent}>
          <LanguageSwitchProvider>
            <AuthContextProvider
              authenticatedData={initialData.session ?? null}
              key={`${initialData.session?.user.id}-${initialData.session?.user.tier}`}
            >
              <SentryUser />
              <AuthenticationStatusMonitor />
              <TimeProviderWithAuth>
                <PosthogInit>
                  <TooltipProvider>
                    <RootLayout>
                      {children}
                      <GlobalNavigationProgress />
                      <GlobalNavigationInfoBadge />
                    </RootLayout>
                  </TooltipProvider>
                </PosthogInit>
              </TimeProviderWithAuth>
            </AuthContextProvider>
          </LanguageSwitchProvider>
          <UpdateAvailableDialog />
        </CookieConsentProvider>
      </AppMetadataProvider>
    </Document>
  );
}
